// Which custom field columns a user shows, per list scope. Stored as the ordered list of definition ids
// that are turned ON, in the order they were turned on - so "hidden by default" needs no defaults map,
// and the newest field is always the rightmost column. Kept apart from opportunityColumns' native
// visibility map, whose closed key union and visible-by-default merge do not fit runtime columns.
//
// Ids of fields that are no longer available are filtered at render, never deleted here - so a field an
// admin re-enables comes back exactly as the user left it.
import type {CustomFieldColumnDef} from './customFieldColumnModel';

const storageKey = (scope: string): string => `custom-field-columns-${scope}-v1`;

export function readCustomFieldChoices(scope: string): string[] {
	try {
		const raw = localStorage.getItem(storageKey(scope));
		const parsed: unknown = raw ? JSON.parse(raw) : [];
		const filtered = Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
		return Array.from(new Set(filtered));
	} catch {
		return [];
	}
}

export function writeCustomFieldChoices(shown: string[], scope: string): void {
	try {
		localStorage.setItem(storageKey(scope), JSON.stringify(shown));
	} catch {
		/* localStorage unavailable */
	}
}

export const toggleCustomField = (shown: string[], id: string): string[] =>
	shown.includes(id) ? shown.filter(s => s !== id) : [...shown, id];

/** `available` is A-Z (toCustomFieldColumnDefs), so the appended block is A-Z too. */
export const showAllCustomFields = (shown: string[], available: CustomFieldColumnDef[]): string[] => [
	...shown,
	...available.filter(f => !shown.includes(f.id)).map(f => f.id),
];

export const hideAllCustomFields = (shown: string[], available: CustomFieldColumnDef[]): string[] => {
	const availableIds = new Set(available.map(f => f.id));
	return shown.filter(id => !availableIds.has(id));
};

export const visibleCustomFields = (shown: string[], available: CustomFieldColumnDef[]): CustomFieldColumnDef[] => {
	const byId = new Map(available.map(f => [f.id, f]));
	return shown.map(id => byId.get(id)).filter((f): f is CustomFieldColumnDef => Boolean(f));
};
